import { Box, HStack, Tag, Text, VStack } from "@chakra-ui/react";
import React from "react";
import { entityProfileModalColors } from "../entityProfileModal.theme";

type EntityProfileAvailabilityRow<TDay extends string, TTimeFrameId extends PropertyKey> = {
  day: TDay;
  timeframesIds: TTimeFrameId[];
};

type EntityProfileAvailabilityTimeFrame<TTimeFrameId extends PropertyKey> = {
  id: TTimeFrameId;
  endTime: number;
  name: string;
  startTime: number;
};

type Props<TDay extends string, TTimeFrameId extends PropertyKey> = {
  availability: EntityProfileAvailabilityRow<TDay, TTimeFrameId>[];
  days: readonly TDay[];
  isDisabled?: boolean;
  timeFrames: EntityProfileAvailabilityTimeFrame<TTimeFrameId>[];
  onChange: (availability: EntityProfileAvailabilityRow<TDay, TTimeFrameId>[]) => void;
};

function formatDay(day: string) {
  return day.slice(0, 3);
}

function formatTime(value: number) {
  const hours = Math.floor(value / 100);
  const minutes = value % 100;

  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
}

export default function EntityProfileAvailabilityGrid<
  TDay extends string,
  TTimeFrameId extends PropertyKey
>(props: Props<TDay, TTimeFrameId>) {
  const timeFrameById = React.useMemo(
    () => new Map(props.timeFrames.map((timeFrame) => [timeFrame.id, timeFrame])),
    [props.timeFrames]
  );
  const availabilityByDay = React.useMemo(
    () =>
      new Map(props.availability.map((dayAvailability) => [dayAvailability.day, dayAvailability])),
    [props.availability]
  );
  const timeFrameIds = React.useMemo(
    () => props.timeFrames.map((timeFrame) => timeFrame.id),
    [props.timeFrames]
  );

  const handleToggleDay = (day: TDay, isAvailable: boolean) => {
    props.onChange(
      props.days.map((candidateDay) => {
        const currentDayAvailability = availabilityByDay.get(candidateDay);

        if (candidateDay !== day) {
          return {
            day: candidateDay,
            timeframesIds: currentDayAvailability?.timeframesIds ?? [],
          };
        }

        return {
          day,
          timeframesIds: isAvailable ? [] : timeFrameIds,
        };
      })
    );
  };

  const handleToggleTimeFrame = (day: TDay, timeFrameId: TTimeFrameId) => {
    props.onChange(
      props.days.map((candidateDay) => {
        const currentDayAvailability = availabilityByDay.get(candidateDay);
        const currentIds = currentDayAvailability?.timeframesIds ?? [];

        if (candidateDay !== day) {
          return {
            day: candidateDay,
            timeframesIds: currentIds,
          };
        }

        return {
          day,
          timeframesIds: currentIds.includes(timeFrameId)
            ? currentIds.filter((currentId) => currentId !== timeFrameId)
            : [...currentIds, timeFrameId],
        };
      })
    );
  };

  return (
    <VStack alignItems="stretch" spacing={3}>
      <HStack
        color={entityProfileModalColors.labelText}
        fontSize="13px"
        fontWeight={700}
        spacing={6}
      >
        <Box minW="88px">Days</Box>
        <Box>Visit starts</Box>
      </HStack>
      {props.days.map((day) => {
        const dayAvailability = availabilityByDay.get(day);
        const selectedTimeFrames =
          dayAvailability?.timeframesIds
            .map((timeFrameId) => timeFrameById.get(timeFrameId))
            .filter((timeFrame) => timeFrame !== undefined) ?? [];
        const isAvailable = selectedTimeFrames.length > 0;

        return (
          <HStack
            key={day}
            alignItems="flex-start"
            color={
              isAvailable ? entityProfileModalColors.text : entityProfileModalColors.disabledText
            }
            minH="34px"
            spacing={6}
          >
            <HStack minW="88px" spacing={2}>
              <Box
                _focusVisible={{
                  boxShadow: `0 0 0 2px ${entityProfileModalColors.active}`,
                  outline: "none",
                }}
                aria-checked={isAvailable}
                aria-label={`${formatDay(day)} availability`}
                as="button"
                bg={
                  isAvailable
                    ? entityProfileModalColors.activeMuted
                    : entityProfileModalColors.surface
                }
                border="1px solid"
                borderColor={
                  isAvailable
                    ? entityProfileModalColors.activeMuted
                    : entityProfileModalColors.inputBorder
                }
                borderRadius="2px"
                boxSize="14px"
                disabled={props.isDisabled}
                role="checkbox"
                type="button"
                onClick={() => handleToggleDay(day, isAvailable)}
              />
              <Text fontSize="14px" fontWeight={600}>
                {formatDay(day)}
              </Text>
            </HStack>
            <HStack alignItems="flex-start" flexWrap="wrap" spacing={2}>
              {selectedTimeFrames.length === 0 ? (
                <Text color={entityProfileModalColors.disabledText} fontSize="14px">
                  Not available
                </Text>
              ) : (
                props.timeFrames.map((timeFrame) => {
                  const selected = dayAvailability?.timeframesIds.includes(timeFrame.id) === true;

                  return (
                    <Tag
                      key={String(timeFrame.id)}
                      as="button"
                      borderRadius="4px"
                      colorScheme={selected ? "blue" : "gray"}
                      cursor={props.isDisabled || !isAvailable ? "not-allowed" : "pointer"}
                      disabled={props.isDisabled || !isAvailable}
                      minH="30px"
                      px={3}
                      type="button"
                      variant={selected ? "solid" : "outline"}
                      onClick={() => handleToggleTimeFrame(day, timeFrame.id)}
                    >
                      {timeFrame.name} ({formatTime(timeFrame.startTime)}-
                      {formatTime(timeFrame.endTime)})
                    </Tag>
                  );
                })
              )}
            </HStack>
          </HStack>
        );
      })}
    </VStack>
  );
}
